import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { FileCheck, ArrowLeft, Save, FileText, Link as LinkIcon, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface EditConfirmationProps {
  kbData: any;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export const EditConfirmation = ({ kbData, onSubmit, onBack, isSubmitting }: EditConfirmationProps) => {
  // Fetch selected domains
  const { data: selectedDomains } = useQuery({
    queryKey: ['selected-domains', kbData.domainIds],
    queryFn: async () => {
      if (!kbData.domainIds || kbData.domainIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('domains')
        .select('id, name')
        .in('id', kbData.domainIds);
      
      if (error) throw error;
      return data;
    },
    enabled: !!kbData.domainIds && kbData.domainIds.length > 0
  });

  const domainNames = selectedDomains?.map(d => d.name) || [];

  return (
    <Card className="border-2 border-black">
      <CardHeader className="bg-slate-100 border-b border-black">
        <CardTitle className="font-serif flex items-center">
          <FileCheck className="mr-2" size={24} />
          Review Changes
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
              <div className="col-span-2">
                <p className="text-sm text-gray-500 mb-1">Title</p>
                <p className="font-medium">{kbData.title || "N/A"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500 mb-1">Selected Domain Boxes</p>
                {kbData.domainIds && kbData.domainIds.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {domainNames.map((name, index) => (
                      <Badge key={index} variant="outline" className="border-black">
                        {name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium">N/A</p>
                )}
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p>{kbData.description || "N/A"}</p>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold mb-2">Additional Details</h3>
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
              <div>
                <p className="text-sm text-gray-500 mb-1">Primary Keyword</p>
                <p className="font-medium">{kbData.keyword || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Category</p>
                <p className="font-medium capitalize">{kbData.category || "N/A"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500 mb-1">Tags</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {kbData.tags && kbData.tags.length > 0 ? (
                    kbData.tags.map((tag: string, index: number) => (
                      <Badge key={index} variant="outline" className="border-black">
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-500">No tags added</span>
                  )}
                </div>
              </div>
              {kbData.notes && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Notes</p>
                  <p>{kbData.notes}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="text-lg font-semibold mb-2">Content Sources</h3>
            <div className="space-y-3 bg-gray-50 p-4 rounded-md">
              <div>
                <p className="text-sm text-gray-500 mb-2">Documents</p>
                {kbData.uploadedFiles && kbData.uploadedFiles.length > 0 ? (
                  <div className="space-y-2">
                    {kbData.uploadedFiles.map((file: any, index: number) => (
                      <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                        <FileText size={16} />
                        <span className="text-sm">{file.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No documents uploaded</p>
                )}
              </div>

              {kbData.links && kbData.links.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">External Links</p>
                  <div className="space-y-2">
                    {kbData.links.map((link: string, index: number) => (
                      <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                        <LinkIcon size={16} />
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline truncate"
                        >
                          {link}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Clicking "Update Knowledge Base" will save all your changes to the existing knowledge base.
            </p>
          </div>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isSubmitting}
              className="border-2 border-black"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="bg-black hover:bg-black/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Update Knowledge Base
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
